import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash, Pencil, Envelope, CheckCircle } from "@phosphor-icons/react";

export default function AdminPublicContent() {
  return (
    <Tabs defaultValue="blog">
      <TabsList>
        <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
        <TabsTrigger value="map" data-testid="tab-map-locs">Mappa</TabsTrigger>
        <TabsTrigger value="contact" data-testid="tab-contact-info">Contatti</TabsTrigger>
        <TabsTrigger value="policy" data-testid="tab-policy">Privacy Policy</TabsTrigger>
        <TabsTrigger value="terms" data-testid="tab-terms">Termini di Servizio</TabsTrigger>
        <TabsTrigger value="entity-fees" data-testid="tab-entity-fees-policy">Policy diritti ente</TabsTrigger>
      </TabsList>
      <TabsContent value="blog"><BlogAdmin /></TabsContent>
      <TabsContent value="map"><MapAdmin /></TabsContent>
      <TabsContent value="contact"><ContactAdmin /></TabsContent>
      <TabsContent value="policy"><PolicyAdmin slug="privacy" /></TabsContent>
      <TabsContent value="terms"><PolicyAdmin slug="terms" /></TabsContent>
      <TabsContent value="entity-fees"><EntityFeesPolicyAdmin /></TabsContent>
    </Tabs>
  );
}

function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: "", title: "", subtitle: "", body: "", cover_image_url: "", author_name: "", published: true });
  const load = () => api.get("/admin/blog").then(r => setPosts(r.data));
  useEffect(() => { load(); }, []);
  const open = (p = null) => {
    setEditing(p);
    setForm(p ? { ...p } : { slug: "", title: "", subtitle: "", body: "", cover_image_url: "", author_name: "", published: true });
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/admin/blog/${editing._id}`, form);
      else await api.post("/admin/blog", form);
      toast.success("Salvato"); setEditing(null); setForm({ slug: "", title: "", subtitle: "", body: "", cover_image_url: "", author_name: "", published: true }); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const del = async (id) => { if (!confirm("Eliminare?")) return; await api.delete(`/admin/blog/${id}`); load(); };

  return (
    <div className="kpi-card">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-display text-xl font-semibold">Articoli Blog</h3>
        <Button onClick={() => open()} size="sm" className="ht-gradient border-0" data-testid="new-post-btn"><Plus size={14} /> Nuovo articolo</Button>
      </div>
      <div className="space-y-2">
        {posts.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessun articolo pubblicato</p>}
        {posts.map(p => (
          <div key={p._id} className="flex items-center justify-between border-b py-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium">{p.title}</div>
              <div className="text-xs text-muted-foreground">/blog/{p.slug} · {p.published ? "Pubblicato" : "Bozza"}</div>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => open(p)}><Pencil size={14} /></Button>
              <Button size="sm" variant="ghost" onClick={() => del(p._id)}><Trash size={14} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing || (form.title && !editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Modifica" : "Nuovo"} articolo</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Slug URL</Label><Input required value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="es. novita-2026" data-testid="post-slug" /></div>
            <div><Label>Titolo</Label><Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="post-title" /></div>
            <div><Label>Sottotitolo</Label><Input value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} /></div>
            <div><Label>URL immagine cover</Label><Input value={form.cover_image_url} onChange={e => setForm({ ...form, cover_image_url: e.target.value })} /></div>
            <div><Label>Testo</Label><Textarea rows={8} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} data-testid="post-body" /></div>
            <div><Label>Autore</Label><Input value={form.author_name} onChange={e => setForm({ ...form, author_name: e.target.value })} /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} />
              <Label>Pubblicato</Label>
            </div>
            <Button type="submit" className="w-full ht-gradient border-0">Salva</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MapAdmin() {
  const [locs, setLocs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", address: "", city: "", latitude: 45.4642, longitude: 9.19, service_type: "", completed_at: "", notes: "" });
  const load = () => api.get("/public/map-locations").then(r => setLocs(r.data));
  useEffect(() => { load(); }, []);
  const open = (l = null) => {
    setEditing(l);
    setForm(l ? { ...l } : { title: "", address: "", city: "", latitude: 45.4642, longitude: 9.19, service_type: "", completed_at: "", notes: "" });
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, latitude: Number(form.latitude), longitude: Number(form.longitude) };
      if (editing) await api.patch(`/admin/map-locations/${editing._id}`, payload);
      else await api.post("/admin/map-locations", payload);
      toast.success("Salvato"); setEditing(null); load();
    } catch (err) { toast.error("Errore"); }
  };
  const del = async (id) => { if (!confirm("Eliminare?")) return; await api.delete(`/admin/map-locations/${id}`); load(); };

  return (
    <div className="kpi-card">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-display text-xl font-semibold">Locations Mappa</h3>
        <Button onClick={() => open()} size="sm" className="ht-gradient border-0"><Plus size={14} /> Nuova location</Button>
      </div>
      <div className="space-y-2">
        {locs.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessuna location</p>}
        {locs.map(l => (
          <div key={l._id} className="flex items-center justify-between border-b py-3">
            <div>
              <div className="font-medium">{l.title}</div>
              <div className="text-xs text-muted-foreground">{l.address}, {l.city} · {l.latitude}, {l.longitude}</div>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => open(l)}><Pencil size={14} /></Button>
              <Button size="sm" variant="ghost" onClick={() => del(l._id)}><Trash size={14} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing || (form.title && !editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Location</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Titolo</Label><Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Indirizzo</Label><Input required value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Città</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Latitudine</Label><Input type="number" step="0.0001" value={form.latitude} onChange={e => setForm({ ...form, latitude: e.target.value })} /></div>
              <div><Label>Longitudine</Label><Input type="number" step="0.0001" value={form.longitude} onChange={e => setForm({ ...form, longitude: e.target.value })} /></div>
            </div>
            <div><Label>Tipo servizio</Label><Input value={form.service_type} onChange={e => setForm({ ...form, service_type: e.target.value })} placeholder="es. APE, Catastale..." /></div>
            <div><Label>Note</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            <Button type="submit" className="w-full ht-gradient border-0">Salva</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContactAdmin() {
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/public/contact").then(r => setInfo(r.data)); }, []);
  const save = async () => {
    await api.put("/admin/contact", info);
    toast.success("Contatti salvati");
  };
  if (!info) return null;
  const upd = (k) => (e) => setInfo({ ...info, [k]: e.target.value });

  return (
    <div className="kpi-card max-w-2xl">
      <h3 className="font-display text-xl font-semibold mb-4">Informazioni di contatto</h3>
      <div className="space-y-3">
        <div><Label>Testo intro pagina Contatti</Label><Textarea value={info.intro_text || ""} onChange={upd("intro_text")} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Email</Label><Input type="email" value={info.email || ""} onChange={upd("email")} data-testid="ci-email" /></div>
          <div><Label>Telefono</Label><Input value={info.phone || ""} onChange={upd("phone")} /></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Indirizzo</Label><Input value={info.address || ""} onChange={upd("address")} /></div>
          <div><Label>Città</Label><Input value={info.city || ""} onChange={upd("city")} /></div>
        </div>
        <div><Label>Orari</Label><Input value={info.hours || ""} onChange={upd("hours")} placeholder="Lun-Ven 9-18" /></div>
        <div><Label>Sito web</Label><Input value={info.website || ""} onChange={upd("website")} /></div>
        <div className="grid grid-cols-3 gap-2">
          <div><Label>Facebook</Label><Input value={info.social_facebook || ""} onChange={upd("social_facebook")} /></div>
          <div><Label>Instagram</Label><Input value={info.social_instagram || ""} onChange={upd("social_instagram")} /></div>
          <div><Label>LinkedIn</Label><Input value={info.social_linkedin || ""} onChange={upd("social_linkedin")} /></div>
        </div>
        <Button onClick={save} className="ht-gradient border-0" data-testid="save-contact">Salva contatti</Button>
      </div>
    </div>
  );
}

function PolicyAdmin({ slug = "privacy" }) {
  const [policies, setPolicies] = useState([]);
  const [editing, setEditing] = useState(null); // policy being edited or "new"
  const [saving, setSaving] = useState(false);

  const label = slug === "terms" ? "Termini di Servizio" : "Privacy Policy";
  const defaultTitle = slug === "terms"
    ? "Termini di Servizio Housetag"
    : "Informativa Privacy Housetag";

  const load = () => api.get(`/admin/policies?slug=${slug}`).then(r => setPolicies(r.data));
  useEffect(() => { load(); }, [slug]);

  const startNew = async () => {
    // Seed from currently active policy (per slug)
    let base = { version: "", title: defaultTitle, body: "", is_active: false, slug };
    const active = policies.find(p => p.is_active);
    if (active) base = { ...base, body: active.body };
    setEditing({ ...base, _new: true });
  };

  const save = async () => {
    if (!editing.version.trim()) { toast.error("Inserisci una versione"); return; }
    setSaving(true);
    try {
      const payload = {
        version: editing.version, title: editing.title, body: editing.body,
        is_active: editing.is_active, slug,
      };
      if (editing._new) {
        await api.post("/admin/policies", payload);
        toast.success(`${label} creata`);
      } else {
        await api.patch(`/admin/policies/${editing._id}`, payload);
        toast.success(`${label} aggiornata`);
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare definitivamente la v${p.version}?`)) return;
    try { await api.delete(`/admin/policies/${p._id}`); toast.success("Eliminata"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const activate = async (p) => {
    if (!window.confirm(`Attivare v${p.version}? Verrà mostrata su /${slug === "terms" ? "terms" : "privacy"} a tutti i visitatori.`)) return;
    try { await api.post(`/admin/policies/${p._id}/activate`); toast.success("Attivata"); load(); }
    catch (err) { toast.error("Errore"); }
  };

  if (editing) {
    return (
      <div className="kpi-card">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-display text-xl font-semibold">
            {editing._new ? `Nuova versione — ${label}` : `Modifica ${label} v${editing.version}`}
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>Annulla</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <Label>Versione</Label>
            <Input value={editing.version} onChange={e => setEditing({ ...editing, version: e.target.value })} placeholder="es. 2.0" data-testid="policy-version" />
          </div>
          <div className="md:col-span-2">
            <Label>Titolo</Label>
            <Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} data-testid="policy-title" />
          </div>
        </div>
        <div>
          <Label>Testo integrale (Markdown supportato)</Label>
          <Textarea rows={20} value={editing.body} onChange={e => setEditing({ ...editing, body: e.target.value })} className="font-mono text-xs" data-testid="policy-body" />
          <p className="text-[11px] text-muted-foreground mt-1">
            Puoi usare Markdown: <code>## Titolo</code>, <code>**grassetto**</code>, <code>- elenco</code>, <code>[link](url)</code>.
          </p>
        </div>
        <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
          <input type="checkbox" checked={!!editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} data-testid="policy-is-active" />
          Imposta come versione attiva (pubblicata sulla pagina /{slug === "terms" ? "terms" : "privacy"})
        </label>
        <Button onClick={save} disabled={saving} className="mt-4 ht-gradient border-0" data-testid="save-policy">
          {saving ? "Salvataggio..." : (editing._new ? "Crea versione" : "Salva modifiche")}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="font-display text-xl font-semibold">{label}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Gestisci versioni multiple. Una sola versione può essere attiva alla volta —
            quella attiva viene servita sulla pagina pubblica <code>/{slug === "terms" ? "terms" : "privacy"}</code>.
          </p>
        </div>
        <Button className="ht-gradient border-0" onClick={startNew} data-testid="new-policy-btn">+ Nuova versione</Button>
      </div>
      <div className="kpi-card p-0 overflow-hidden">
        {policies.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Nessuna versione caricata. Clicca "+ Nuova versione" per pubblicare il testo su /{slug === "terms" ? "terms" : "privacy"}.
          </div>
        ) : (
          <table className="w-full text-sm mobile-cards">
            <thead><tr className="text-left border-b">
              <th className="py-2 px-4">Versione</th>
              <th>Titolo</th>
              <th>Ultima modifica</th>
              <th className="text-center">Attiva</th>
              <th className="text-right pr-4">Azioni</th>
            </tr></thead>
            <tbody>
              {policies.map(p => (
                <tr key={p._id} className="border-b hover:bg-[color:hsl(var(--surface-alt))]" data-testid={`policy-row-${p._id}`}>
                  <td data-label="Versione" className="py-3 px-4 font-mono font-semibold">v{p.version}</td>
                  <td data-label="Titolo">{p.title}</td>
                  <td data-label="Ultima modifica" className="text-muted-foreground text-xs">
                    {p.updated_at ? new Date(p.updated_at).toLocaleString("it-IT") : "—"}
                  </td>
                  <td data-label="Attiva" className="text-center">
                    {p.is_active ? (
                      <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">ATTIVA</span>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => activate(p)} data-testid={`activate-${p._id}`}>Attiva</Button>
                    )}
                  </td>
                  <td data-label="Azioni" className="text-right pr-4 mobile-actions">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-policy-${p._id}`}>Modifica</Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(p)} className="text-red-600 hover:text-red-700" data-testid={`delete-policy-${p._id}`}>Elimina</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ==================== ENTITY FEES POLICY ADMIN ====================
// Testo informativo sui diritti ente mostrato al pro in prenotazione (modal)
// e al cliente finale nel checkout pubblico /pay/{pid} (con checkbox obbligatoria).
function EntityFeesPolicyAdmin() {
  const [content, setContent] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/public/entity-fees-policy")
      .then((r) => {
        setContent(r.data?.content_html || "");
        setUpdatedAt(r.data?.updated_at || null);
      })
      .catch(() => toast.error("Errore caricamento policy diritti ente"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    if (!content.trim()) {
      toast.error("Il contenuto non può essere vuoto");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/entity-fees-policy", { content_html: content });
      setUpdatedAt(r.data?.updated_at || null);
      toast.success("Policy diritti ente aggiornata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-muted-foreground p-4">Caricamento…</div>;

  return (
    <div className="space-y-4 mt-4" data-testid="entity-fees-policy-admin">
      <div className="text-xs text-slate-600 bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
        Questa informativa è mostrata al <strong>professionista</strong> durante la prenotazione
        di un servizio con oneri Ente e al <strong>cliente finale</strong> nel checkout pubblico
        (con checkbox obbligatoria). Puoi usare tag HTML base (<code>&lt;p&gt;</code>, <code>&lt;strong&gt;</code>, <code>&lt;br/&gt;</code>, <code>&lt;ul&gt;</code>).
      </div>
      <div>
        <Label>Contenuto (HTML)</Label>
        <Textarea
          rows={12}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="font-mono text-xs"
          data-testid="entity-fees-policy-content"
        />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-1">Anteprima</div>
        <div
          className="border rounded-md p-3 bg-slate-50 text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: content }}
          data-testid="entity-fees-policy-preview"
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {updatedAt ? `Ultimo aggiornamento: ${new Date(updatedAt).toLocaleString("it-IT")}` : "Mai modificata"}
        </div>
        <Button onClick={save} disabled={saving} data-testid="entity-fees-policy-save">
          {saving ? "Salvataggio…" : "Salva"}
        </Button>
      </div>
    </div>
  );
}

